import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Loader from "@/components/Loader";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import {
  MENU_KEYS,
  MENU_LABELS,
  CONTENT_KINDS,
  CONTENT_KIND_LABELS,
  ROLES,
  getRolePreset,
} from "@/lib/permissions";

const MENU_ORDER = [
  MENU_KEYS.ACCUEIL,
  MENU_KEYS.RESERVATIONS,
  MENU_KEYS.BILLETTERIE,
  MENU_KEYS.CONTENU,
  MENU_KEYS.ADMINS,
  MENU_KEYS.PERSONNALISATION,
];

function emptyEditing() {
  return {
    email: "",
    name: "",
    password: "",
    role: "ateliers_externe",
    isSuperAdmin: false,
    allowedMenus: getRolePreset("ateliers_externe").menus,
    allowedContentKinds: getRolePreset("ateliers_externe").kinds,
    allowedAteliers: [],
  };
}

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [ateliers, setAteliers] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data: u } = await api.get("/admin/users");
    const { data: a } = await api.get("/admin/all-ateliers");
    setUsers(u);
    setAteliers(a);
  };
  useEffect(() => { load().catch(() => setUsers([])); }, []);

  const openNew = () => setEditing(emptyEditing());
  const openEdit = (u) => {
    const role = u.role || (u.isSuperAdmin ? "super" : "custom");
    setEditing({
      ...u,
      password: "",
      role,
      isSuperAdmin: !!u.isSuperAdmin,
      allowedMenus: u.allowedMenus || [],
      allowedContentKinds: u.allowedContentKinds || [],
      allowedAteliers: u.allowedAteliers || [],
    });
  };

  const applyRolePreset = (roleKey) => {
    const preset = getRolePreset(roleKey);
    setEditing((cur) => ({
      ...cur,
      role: roleKey,
      isSuperAdmin: preset.isSuper,
      allowedMenus: [...preset.menus],
      allowedContentKinds: [...preset.kinds],
      // Keep existing allowedAteliers unless role no longer needs it
      allowedAteliers: preset.limitByAteliers ? (cur.allowedAteliers || []) : (preset.key === "super" ? [] : (cur.allowedAteliers || [])),
    }));
  };

  const toggleMenu = (menuKey) => {
    setEditing((cur) => {
      const set = new Set(cur.allowedMenus || []);
      if (set.has(menuKey)) set.delete(menuKey); else set.add(menuKey);
      return { ...cur, allowedMenus: Array.from(set), role: "custom" };
    });
  };

  const toggleKind = (kind) => {
    setEditing((cur) => {
      const set = new Set(cur.allowedContentKinds || []);
      if (set.has(kind)) set.delete(kind); else set.add(kind);
      return { ...cur, allowedContentKinds: Array.from(set), role: "custom" };
    });
  };

  const toggleAtelier = (id) => {
    setEditing((cur) => {
      const set = new Set(cur.allowedAteliers || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...cur, allowedAteliers: Array.from(set) };
    });
  };

  const save = async () => {
    try {
      const body = {
        email: editing.email,
        name: editing.name,
        role: editing.role,
        isSuperAdmin: !!editing.isSuperAdmin,
        allowedMenus: editing.isSuperAdmin ? [] : (editing.allowedMenus || []),
        allowedContentKinds: editing.isSuperAdmin ? [] : (editing.allowedContentKinds || []),
        allowedAteliers: editing.allowedAteliers || [],
      };
      if (editing.password) body.password = editing.password;
      if (editing.id) {
        await api.put(`/admin/users/${editing.id}`, body);
        toast.success("Admin mis à jour");
      } else {
        await api.post("/admin/users", body);
        toast.success("Admin créé");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Supprimer ${u.name} ?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Supprimé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const showAteliers = useMemo(() => {
    if (!editing) return false;
    if (editing.isSuperAdmin) return false;
    // Show ateliers picker if the user has 'reservations' menu (which is where allowedAteliers filtering applies)
    return (editing.allowedMenus || []).includes(MENU_KEYS.RESERVATIONS);
  }, [editing]);

  const showKinds = useMemo(() => {
    if (!editing) return false;
    if (editing.isSuperAdmin) return false;
    return (editing.allowedMenus || []).includes(MENU_KEYS.CONTENU);
  }, [editing]);

  if (!users) return <Loader />;

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div>
          <h1 className="text-3xl font-black">Admins</h1>
          <p className="text-neutral-400 text-sm">Gérer les comptes administrateurs, leurs rôles et leurs droits d'accès</p>
        </div>
        <button onClick={openNew} className="brand-gradient-btn px-4 py-2 rounded-lg" data-testid="new-admin-btn">+ Nouvel admin</button>
      </div>

      {users.length === 0 ? (
        <EmptyState title="Aucun admin" subtitle="Créez le premier admin pour partager l'accès à la console." />
      ) : (
        <div className="space-y-2">
          {users.map((u) => {
            const isSuper = !!u.isSuperAdmin || (u.role === "super") || (!(u.allowedMenus || []).length && !(u.allowedAteliers || []).length && u.role === undefined);
            const role = u.role || (isSuper ? "super" : "custom");
            return (
              <div key={u.id} className="card-hsg p-4 flex flex-wrap gap-3 items-center">
                <div className="flex-1 min-w-56">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold">{u.name}</div>
                    {isSuper ? (
                      <span className="chip" style={{ background: "#A1C86122", color: "#A1C861", border: "1px solid #A1C86155" }}>ADMIN</span>
                    ) : (
                      <span className="chip" style={{ background: "#5BC5F222", color: "#5BC5F2", border: "1px solid #5BC5F255" }}>{role.toUpperCase().replace(/_/g, " ")}</span>
                    )}
                  </div>
                  <div className="text-sm text-neutral-400">{u.email}</div>
                  {!isSuper && (
                    <div className="text-xs text-neutral-500 mt-1 flex gap-2 flex-wrap">
                      {(u.allowedMenus || []).length > 0 && <span>📋 Menus: {u.allowedMenus.length}</span>}
                      {(u.allowedContentKinds || []).length > 0 && <span>📝 Sections: {u.allowedContentKinds.join(", ")}</span>}
                      {(u.allowedAteliers || []).length > 0 && <span>🎨 Ateliers: {u.allowedAteliers.length}</span>}
                    </div>
                  )}
                </div>
                <button onClick={() => openEdit(u)} className="px-3 py-1 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-sm" data-testid={`edit-admin-${u.id}`}>✏️ Modifier</button>
                <button onClick={() => del(u)} className="px-3 py-1 rounded-lg bg-red-900/40 hover:bg-red-900/70 text-sm" data-testid={`delete-admin-${u.id}`}>🗑️</button>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="card-hsg p-6 w-full max-w-2xl max-h-[90vh] overflow-auto scrollbar-thin" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">{editing?.id ? "Modifier admin" : "Nouvel admin"}</h2>

            {/* Base fields */}
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase text-neutral-400">Nom *</span>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" data-testid="edit-name" />
              </label>
              <label className="block">
                <span className="text-xs uppercase text-neutral-400">Email *</span>
                <input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="mt-1 w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" data-testid="edit-email" />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs uppercase text-neutral-400">Mot de passe {editing.id ? "(laisser vide pour ne pas changer)" : "*"}</span>
                <input type="password" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className="mt-1 w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" data-testid="edit-password" />
              </label>
            </div>

            {/* Role preset */}
            <div className="mt-5">
              <div className="text-xs uppercase text-neutral-400 mb-1">Rôle prédéfini</div>
              <select value={editing.role} onChange={(e) => applyRolePreset(e.target.value)} className="w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" data-testid="edit-role">
                {ROLES.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
              <div className="text-xs text-neutral-500 mt-1">
                Sélectionner un rôle applique les droits recommandés. Vous pouvez ensuite ajuster chaque case ci-dessous.
              </div>
            </div>

            {/* Menus checkboxes */}
            {!editing.isSuperAdmin && (
              <div className="mt-5">
                <div className="text-xs uppercase text-neutral-400 mb-2">Menus de la console accessibles</div>
                <div className="grid grid-cols-2 gap-2">
                  {MENU_ORDER.map((k) => {
                    const meta = MENU_LABELS[k];
                    const on = (editing.allowedMenus || []).includes(k);
                    return (
                      <label key={k} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${on ? "bg-neutral-800 border-[#5BC5F2]/50" : "bg-neutral-900 border-neutral-700 hover:border-neutral-600"}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleMenu(k)} data-testid={`menu-${k}`} />
                        <span className="text-lg">{meta.emoji}</span>
                        <span className="text-sm">{meta.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Content kinds (only shown when 'contenu' is enabled) */}
            {showKinds && (
              <div className="mt-5">
                <div className="text-xs uppercase text-neutral-400 mb-2">Sections de contenu accessibles</div>
                <div className="grid grid-cols-3 gap-2">
                  {CONTENT_KINDS.map((k) => {
                    const meta = CONTENT_KIND_LABELS[k];
                    const on = (editing.allowedContentKinds || []).includes(k);
                    return (
                      <label key={k} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${on ? "bg-neutral-800 border-[#5BC5F2]/50" : "bg-neutral-900 border-neutral-700 hover:border-neutral-600"}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleKind(k)} data-testid={`kind-${k}`} />
                        <span>{meta.emoji}</span>
                        <span className="text-sm">{meta.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Ateliers picker (only shown when 'reservations' menu is enabled) */}
            {showAteliers && (
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase text-neutral-400">
                    Ateliers autorisés
                    <span className="ml-2 normal-case text-neutral-500">({(editing.allowedAteliers || []).length}/{ateliers.length})</span>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setEditing((c) => ({ ...c, allowedAteliers: ateliers.map((a) => a.id) }))} className="text-xs text-[#5BC5F2] hover:underline">Tout</button>
                    <button type="button" onClick={() => setEditing((c) => ({ ...c, allowedAteliers: [] }))} className="text-xs text-neutral-400 hover:underline">Aucun</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1 max-h-56 overflow-y-auto scrollbar-thin bg-neutral-900 border border-neutral-700 rounded-lg p-2">
                  {ateliers.map((a) => {
                    const on = (editing.allowedAteliers || []).includes(a.id);
                    return (
                      <label key={a.id} className="flex items-center gap-2 text-xs px-2 py-1 hover:bg-neutral-800 rounded cursor-pointer">
                        <input type="checkbox" checked={on} onChange={() => toggleAtelier(a.id)} />
                        <span className="truncate">{a.no != null ? `(${a.no}) ` : ""}{a.title}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  Le compte ne verra que les réservations des ateliers cochés. Laissez tout coché pour un accès global aux réservations.
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="flex-1 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700">Annuler</button>
              <button onClick={save} className="flex-1 brand-gradient-btn py-2 rounded-lg" data-testid="save-admin">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
